import { useEffect, useState } from 'react';

/**
 * Minimal client-side path tracking (no router library — SPEC-001 REQ-065
 * "no package without a comparably simple native alternative"; SPEC-002
 * only needs 3 routes: /login, /app, /app/profile).
 */
const NAVIGATE_EVENT = 'df:navigate';

/** `path` may include a query string (e.g. `/app?status=archived`). */
export function navigate(path: string): void {
  const current = window.location.pathname + window.location.search;
  if (current === path) return;
  window.history.pushState({}, '', path);
  window.dispatchEvent(new Event(NAVIGATE_EVENT));
}

/**
 * SPEC-003 plan §28: reflects dashboard filters in the URL query string
 * without a router dependency. Replaces the current entry (no new
 * history stack entry per keystroke/filter change).
 */
export function replaceQuery(params: URLSearchParams): void {
  const query = params.toString();
  const next = window.location.pathname + (query ? `?${query}` : '');
  if (next === window.location.pathname + window.location.search) return;
  window.history.replaceState({}, '', next);
  window.dispatchEvent(new Event(NAVIGATE_EVENT));
}

export function useCurrentPath(): string {
  const [path, setPath] = useState(window.location.pathname);

  useEffect(() => {
    const onChange = () => setPath(window.location.pathname);
    window.addEventListener('popstate', onChange);
    window.addEventListener(NAVIGATE_EVENT, onChange);
    return () => {
      window.removeEventListener('popstate', onChange);
      window.removeEventListener(NAVIGATE_EVENT, onChange);
    };
  }, []);

  return path;
}

/**
 * Returns the raw query string (e.g. `"?q=x"`), not a `URLSearchParams`
 * instance — a fresh `URLSearchParams` object is a new reference on every
 * call even when its content is unchanged, which breaks memoization for
 * any consumer that depends on it (an infinite effect loop, in
 * `useFunnelListQuery`). Callers that need `URLSearchParams` should build
 * one from this string inside their own `useMemo`.
 */
export function useCurrentSearch(): string {
  const [search, setSearch] = useState(window.location.search);

  useEffect(() => {
    const onChange = () => setSearch(window.location.search);
    window.addEventListener('popstate', onChange);
    window.addEventListener(NAVIGATE_EVENT, onChange);
    return () => {
      window.removeEventListener('popstate', onChange);
      window.removeEventListener(NAVIGATE_EVENT, onChange);
    };
  }, []);

  return search;
}
